import React from 'react';
import { useAppSelector } from '../../../store/hooks';
import { Document, DocumentCategory, FilingType } from '../../../store/document/types';
import styles from './DocumentList.module.scss';

interface DocumentListProps {
  documents: Document[];
  onDocumentClick: (docId: string) => void;
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

/**
 * Removes the ticker prefix from document names if present
 * Example: "AAPL - Q2 2023 Earnings Call.html" becomes "Q2 2023 Earnings Call.html"
 */
const formatDocumentName = (document: Document, companyTicker?: string) => {
  const { name, category, type, fiscal_year, fiscal_quarter } = document;

  // Remove ticker prefix if present
  let baseName = name;
  if (!companyTicker) {
    const tickerPattern = /^([A-Z0-9.]+)\s+-\s+/;
    const match = baseName.match(tickerPattern);
    if (match) {
      baseName = baseName.substring(match[0].length);
    }
  } else {
    const tickerPrefix = `${companyTicker} - `;
    if (baseName.startsWith(tickerPrefix)) {
      baseName = baseName.substring(tickerPrefix.length);
    }
  }

  // Special naming rules
  if (category === DocumentCategory.TRANSCRIPT) {
    // Drop suffix (.html, .pdf) for transcripts
    const lastDotIndex = baseName.lastIndexOf('.');
    if (lastDotIndex > 0) {
      return baseName.substring(0, lastDotIndex);
    }
    return baseName;
  }

  if (category === DocumentCategory.FILING) {
    if (type === FilingType.TEN_K) {
      return `10-K - FY ${fiscal_year}`;
    }
    if (type === FilingType.TWENTY_F) {
      return `20-F - FY ${fiscal_year}`;
    }
    if (type === FilingType.TEN_Q) {
      const quarterLabel = fiscal_quarter ? `Q${fiscal_quarter}` : '';
      return quarterLabel ? `10Q - ${quarterLabel} ${fiscal_year}` : `10Q - ${fiscal_year}`;
    }
  }

  return baseName;
};

const DocumentList: React.FC<DocumentListProps> = ({ documents, onDocumentClick }) => {
  const documentId = useAppSelector((state) => state.document.documentId);
  const loadedCompanyDocs = useAppSelector((state) => state.company.loadedCompanyDocs);

  // Get company ticker for the first document in the list
  const companyTicker =
    documents.length > 0 && loadedCompanyDocs.length > 0
      ? loadedCompanyDocs.find((c) => c.id === documents[0].company_id)?.ticker
      : undefined;

  if (!documents || documents.length === 0) {
    return null;
  }

  // Sort documents by published date (most recent first)
  const sortedDocuments = [...documents].sort((a, b) => {
    if (!a.published_at && !b.published_at) return 0;
    if (!a.published_at) return 1;
    if (!b.published_at) return -1;
    return new Date(b.published_at).getTime() - new Date(a.published_at).getTime();
  });

  return (
    <div className={styles.documentList}>
      {sortedDocuments.map((document) => (
        <div
          key={document.id}
          className={`${styles.documentItem} ${documentId === document.id ? styles.active : ''}`}
          onClick={() => onDocumentClick(document.id)}
        >
          <div className={styles.documentName}>{formatDocumentName(document, companyTicker)}</div>
          <div className={styles.documentDate}>
            {document.published_at ? formatDate(document.published_at) : ''}
          </div>
        </div>
      ))}
    </div>
  );
};

export default DocumentList;
